import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatStepper } from '@angular/material/stepper';
import { PrFormConstructor } from '@app/components/pr-form/pr-form.contructor';
import {
  PledgeRead,
  Product,
} from '@app/forms/subscription/definition/interface/Pledge';
import { FormControlOption } from '@app/forms/subscription/definition/interface/formControlOption.interface';
import { Content } from '@app/forms/subscription/definition/interface/product';
import { HttpApiService } from '@app/providers/http-api/http-api.service';
import { Observable, forkJoin, catchError, map, startWith, take, tap } from 'rxjs';
import { API_ROUTE_PRODUCT } from '../../api/api-router';

import { Validators } from '@angular/forms';
import { PrModalConfirmComponent } from '@app/components/pr-modal/pr-modal-confirm/pr-modal-confirm.component';
import { AttributionDao } from '@app/dao/attribution/attribution.dao';
import { IbanValidatorDao } from '@app/dao/iban-validator/iban-validator.dao';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SubscriptionFacadeService } from '../../services/subscription-facade.service';
import { EaSubscriptionBuilder } from './ea-subscription-form.builder';

interface ProductType {
  type: FormControlOption[];
  name: string;
}

@UntilDestroy()
@Component({
    selector: 'payment-form',
    templateUrl: './payment-form.component.html',
    styleUrls: ['./payment-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class PaymentFormComponent
  extends EaSubscriptionBuilder
  implements OnInit
{
  @ViewChild('linearVerticalStepper', { static: false })
  private steper: MatStepper;
  @ViewChild('invalidIbanModal') invalidIbanModal: PrModalConfirmComponent;

  types: FormControlOption[] = [
    { value: 'TRANSFERT', label: 'Virement', suffix: { title: 'A' } },
    { value: 'CHEQUE', label: 'Chèque', suffix: { title: 'B' } },
    { value: 'DIRECT_DEBIT', label: 'Prélèvement', suffix: { title: 'C' } },
  ];
  products: Product[] = [];
  productsType: ProductType[] = [];
  currentPageNumber: number = 0;
  itemsPerPage: number = 1;
  pledge: PledgeRead;
  productsType$: Observable<any>;
  organizationId: number;
  isModalOpen = false;

  constructor(
    protected formConstructor: PrFormConstructor,
    private subscriptionFacade: SubscriptionFacadeService,
    private httpApi: HttpApiService,
    private attributionDao: AttributionDao,
    private ibanValidatorDao: IbanValidatorDao,
  ) {
    super(formConstructor);
  }

  ngOnInit() {
    this.pledge = this.subscriptionFacade.getPledge();

    this.attributionDao
      .getOrganization()
      .pipe(take(1))
      .subscribe((data: any) => {
        this.organizationId = data.id;
      });

    this.products = this.pledge?.products;

    this.productsType$ = this.getProductsType$().pipe(
      take(1),
      tap((res: any) => {
        this.productsType = res;

        this.initForm(this.pledge.products, {
          productsType: this.productsType,
        });
        this.updateValid(this.currentPageNumber);
      }),
    );
  }
  private subscribeToPaymentTypeChanges(currentPageNumber: number): void {
    this.scpis
      ?.get(`${currentPageNumber}`)
      ?.get('parameters')
      ?.get('paymentType')
      .valueChanges.pipe(
        startWith(
          this.scpis
            ?.get(`${currentPageNumber}`)
            ?.get('parameters')
            ?.get('paymentType')?.value,
        ),
        untilDestroyed(this),
      )

      .subscribe(value => {
        if (value === 'DIRECT_DEBIT') {
          this.setValidatorsForDirectDebit(`${currentPageNumber}`);
          return;
        }
        this.clearValidation(`${currentPageNumber}`, 'directDebitIban');
      });
  }

  private clearValidation(
    currentPageNumber: string,
    controlName: string,
  ): void {
    const control = this.scpis
      .get(currentPageNumber)
      ?.get('parameters')
      ?.get(controlName);
    if (!control) return;

    ['iban', 'bic', 'holder', 'bankName'].forEach(fieldName => {
      const fieldControl = control.get(fieldName);
      if (fieldControl) {
        fieldControl.clearValidators();
        fieldControl.updateValueAndValidity();
      }
    });
  }

  private setValidatorsForDirectDebit(currentPageNumber: string): void {
    const directDebitIban = this.scpis
      .get(currentPageNumber)
      ?.get('parameters')
      ?.get('directDebitIban');
    if (!directDebitIban) return;

    ['iban', 'bic', 'holder', 'bankName'].forEach(fieldName => {
      const fieldControl = directDebitIban.get(fieldName);
      if (fieldControl) {
        fieldControl.setValidators([Validators.required]);
        fieldControl.updateValueAndValidity();
      }
    });
  }
  updateValid(currentPageNumber) {
    this.subscribeToPaymentTypeChanges(currentPageNumber);
    this.scpis
      ?.get(`${currentPageNumber}`)
      ?.get('parameters')
      ?.get('paymentType')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe(value => {
        this.scpis.get(`${currentPageNumber}`)?.get('parameters').reset(
          {
            holderIsSubscriber: true,
            productId: this.products?.[currentPageNumber]?.productId,
            productName: this.products?.[currentPageNumber]?.productName,
            paymentType: value,
          },
          { emitEvent: false },
        );
      });

    this.scpis
      .get(`${currentPageNumber}`)
      ?.get('parameters')
      ?.get('holderIsSubscriber')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe(value => {
        if (value) {
          this.scpis
            .get(`${currentPageNumber}`)
            ?.get('parameters')
            ?.get('interventionReason')
            ?.setValue(null);
          this.scpis
            .get(`${currentPageNumber}`)
            ?.get('parameters')
            ?.get('reltionshipHolderSubscriber')
            .setValue(null);

          this.scpis
            ?.get(`${currentPageNumber}`)
            ?.get('parameters')
            ?.get('reltionshipHolderSubscriber')
            ?.clearValidators();
          this.scpis
            ?.get(`${currentPageNumber}`)
            ?.get('parameters')
            ?.get('reltionshipHolderSubscriber')
            ?.updateValueAndValidity();

          this.scpis
            ?.get(`${currentPageNumber}`)
            ?.get('parameters')
            ?.get('interventionReason')
            ?.clearValidators();
          this.scpis
            ?.get(`${currentPageNumber}`)
            ?.get('parameters')
            ?.get('interventionReason')
            ?.updateValueAndValidity();
        }
      });
  }
  copyFirstFormGroup(index) {
    const {
      chequeAccountHolder,
      chequeBank,
      directDebitBic,
      directDebitHolder,
      directDebitIban,
      holderIsSubscriber,
      interventionReason,
      paymentType,
      iban,
    } = this.scpis.get(`${index}`)?.get('parameters').value;
    const {
      chequeAccountHolder: chequeAccountHolder2,
      chequeBank: chequeBank2,
      directDebitBic: directDebitBic2,
      directDebitHolder: directDebitHolder2,
      directDebitIban: directDebitIban2,
      holderIsSubscriber: holderIsSubscriber2,
      interventionReason: interventionReason2,
      paymentType: paymentType2,
    } = this.scpis.get(`${index + 1}`)?.get('parameters')?.value;

    if (
      (!paymentType2 && paymentType) ||
      (paymentType2 == paymentType &&
        !directDebitHolder2 &&
        !directDebitIban2 &&
        !directDebitBic2)
    ) {
      this.scpis
        .get(`${index + 1}`)
        ?.get('parameters')
        .patchValue({
          chequeAccountHolder,
          chequeBank,
          directDebitBic,
          directDebitHolder,
          directDebitIban,
          holderIsSubscriber,
          interventionReason,
          paymentType,
          iban,
        });
    }
  }

  submitForm() {
    this.subscriptionFacade.submitPledge(
      this,
      'products',
      this.currentPageNumber == this.products.length - 1,
      this.nextStep.bind(this),
      true,
    );
  }

  onSubmit() {
    this.subscriptionFacade.selectErrorInputAndScroll(this);
    const formValues = this.scpis.value;
    const currentForm = formValues[this.currentPageNumber];

    const isDirectDebit = currentForm.parameters.paymentType === 'DIRECT_DEBIT';
    const ibanValue = isDirectDebit
      ? currentForm.parameters.directDebitIban
      : null;

    if (
      this.scpis.controls[this.currentPageNumber]?.valid &&
      this.currentPageNumber == this.products.length - 1
    ) {
      if (isDirectDebit) {
        this.ibanValidatorDao
          .validate(ibanValue)
          .pipe(untilDestroyed(this))
          .subscribe(res => {
            if (!res.valid) {
              this.invalidIbanModal.open();
            } else {
              this.submitForm();
            }
          });
      } else {
        this.submitForm();
      }
    }
    if (
      this.scpis.controls?.[this.currentPageNumber]?.valid &&
      this.currentPageNumber < this.products.length - 1
    ) {
      this.copyFirstFormGroup(this.currentPageNumber);

      if (isDirectDebit) {
        this.ibanValidatorDao
          .validate(ibanValue)
          .pipe(untilDestroyed(this))
          .subscribe(res => {
            if (!res.valid) {
              this.invalidIbanModal.open();
            } else {
              this.submitForm();
            }
          });
      } else {
        this.submitForm();
      }
    }
    this.updateValid(this.currentPageNumber + 1);
  }
  nextPage() {
    this.currentPageNumber++;
  }
  prev() {
    if (this.currentPageNumber == 0) {
      this.subscriptionFacade.previousStep();
    }
    if (this.currentPageNumber > 0) this.currentPageNumber--;
    this.steper.previous();
  }

  nextStep() {
    this.currentPageNumber++;
    this.steper.next();
    this.steper.selected.completed = true;
  }
  prevStep() {
    if (this.currentPageNumber == 0) {
      this.subscriptionFacade.previousStep();
      this.steper.previous();
    }
    if (this.currentPageNumber > 0) this.currentPageNumber--;
  }
  stepClick(ev) {
    this.currentPageNumber = ev.selectedIndex;
  }

  getProductType(id: any) {
    return this.httpApi.get(API_ROUTE_PRODUCT.replace(':id', id)).pipe(
      take(1),
      map((product: Content) => {
        const productName = product.name;

        return {
          type: this.normalizeProduct(product),
          name: productName,
        };
      }),
    );
  }
  normalizeProduct(product: Content): FormControlOption[] {
    const allowedPaymentTypes = product.subscriptionAllowedPaymentTypes;

    return allowedPaymentTypes.map((t, index) => {
      const type = this.types?.find(type => type.value == t);
      return {
        ...type,
        suffix: { title: String.fromCharCode(index + 1 + 64) },
      };
    });
  }

  getProductsType$() {
    let nested: Observable<any>[] = [];

    if (this.pledge?.products?.length) {
      this.pledge.products.forEach(product => {
        nested.push(this.getProductType(product.productId));
      });
    }
    return forkJoin(nested).pipe(
      take(1),

      catchError(err => {
        return err;
      }),
    );
  }
  getType(productName: string) {
    const result = this.productsType?.find(
      product => product.name == productName,
    );

    return result?.type?.length ? result?.type : this.types;
  }

  onConfirmInvalidIban() {
    this.submitForm();
  }

  onCancelInvalidIban() {
    this.invalidIbanModal.close();
  }
}
